import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import { emailBlastApi } from '@/api/emailBlast'
import type { EmailBlastViewDto } from '@/types/emailBlast'
import { useToast } from './useToast'

/**
 * Loads one blast (with engagement) for the deep-linkable detail route (ORB-384) and exposes the
 * resume/cancel controls. Mirrors useEmailBlasts' action handling — Resume surfaces the API's 409
 * (blast not Paused) distinctly — but scoped to a single blast rather than a campaign list.
 */
export function useEmailBlastDetail({ workspaceId, blastId }: { workspaceId: string; blastId: string }) {
  const [blast, setBlast] = useState<EmailBlastViewDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actioning, setActioning] = useState(false)

  const { showSuccess, showError, showErrorUnless422 } = useToast()

  // Generation token: only the latest load applies, so a superseded/unmounted load can't write stale data.
  const requestIdRef = useRef(0)

  const load = useCallback(async () => {
    if (!workspaceId || !blastId) return
    const reqId = ++requestIdRef.current
    setLoading(true)
    setError(null)
    const res = await emailBlastApi.get(workspaceId, blastId)
    if (requestIdRef.current !== reqId) return
    if (res.error) setError(res.error)
    if (res.data) setBlast(res.data)
    setLoading(false)
  }, [workspaceId, blastId])

  useEffect(() => {
    void load()
    return () => { requestIdRef.current++ }
  }, [load])

  const resume = useCallback(async () => {
    setActioning(true)
    try {
      const res = await emailBlastApi.resume(workspaceId, blastId)
      if (res.error || !res.data) {
        if (res.status === 409) {
          showError('Cannot resume', 'This blast is no longer paused. Refreshing its status.')
          await load()
        } else {
          showErrorUnless422('Resume failed', res)
        }
        return
      }
      setBlast(res.data)
      showSuccess('Blast resumed', 'Sending has resumed.')
    } finally {
      setActioning(false)
    }
  }, [workspaceId, blastId, load, showError, showErrorUnless422, showSuccess])

  const cancel = useCallback(async () => {
    setActioning(true)
    try {
      const res = await emailBlastApi.cancel(workspaceId, blastId)
      if (res.error || !res.data) {
        showErrorUnless422('Cancel failed', res)
        return
      }
      setBlast(res.data)
      showSuccess('Blast cancelled', 'No further emails will be sent.')
    } finally {
      setActioning(false)
    }
  }, [workspaceId, blastId, showErrorUnless422, showSuccess])

  return { blast, loading, error, actioning, load, resume, cancel }
}
